/**
 * SR básico de sesión: Cobb, distancias y SUV μ/max de ROI.
 */
import {
  concat,
  elCS,
  elDA,
  elDS,
  elLO,
  elPN,
  elSH,
  elSQ,
  elTM,
  elUI,
  newUid,
  nowDicomDate,
  nowDicomTime,
  wrapPart10,
} from './dicomPart10'

export const ENHANCED_SR_SOP_CLASS = '1.2.840.10008.5.1.4.1.1.88.22'

function dsStr(image, tag) {
  if (!image || !image.data || typeof image.data.string !== 'function') return ''
  try {
    return image.data.string(tag) || ''
  } catch (e) {
    return ''
  }
}

function codeItem(value, scheme, meaning) {
  return concat([
    elSH('x00080100', value),
    elSH('x00080102', scheme),
    elLO('x00080104', meaning),
  ])
}

function numItem(obs) {
  const units = obs.unitCode || { value: '1', scheme: 'UCUM', meaning: 'no units' }
  const measured = concat([
    elSQ('x004008ea', [codeItem(units.value, units.scheme, units.meaning)]),
    elDS('x0040a30a', String(obs.value)),
  ])
  return concat([
    elCS('x0040a010', 'CONTAINS'),
    elCS('x0040a040', 'NUM'),
    elSQ('x0040a043', [codeItem(obs.code, '99UDV', obs.meaning)]),
    elSQ('x0040a300', [measured]),
  ])
}

function numOrNull(n) {
  const v = Number(n)
  return Number.isFinite(v) ? v : null
}

/**
 * @param {Array} measurements
 * @returns {Array<{ kind: string, code: string, meaning: string, value?: number, unit?: string, unitCode?: object, text?: string }>}
 */
export function collectSrObservations(measurements) {
  const out = []
  ;(measurements || []).forEach((m, i) => {
    if (!m) return
    const data = m.data || {}
    const tool = m.tool
    const label = m.note ? ` ${m.note}` : ''
    if (tool === 'Length') {
      const v = numOrNull(data.length)
      if (v == null) return
      out.push({
        kind: 'length',
        code: 'LEN',
        meaning: `Distance${label}`.trim(),
        value: v,
        unit: data.unit || 'mm',
        unitCode: { value: 'mm', scheme: 'UCUM', meaning: 'millimeter' },
        index: i,
      })
      return
    }
    if (tool === 'CobbAngle' || tool === 'Angle') {
      const v = numOrNull(data.rAngle != null ? data.rAngle : data.angle)
      if (v == null) return
      out.push({
        kind: tool === 'CobbAngle' ? 'cobb' : 'angle',
        code: tool === 'CobbAngle' ? 'COBB' : 'ANG',
        meaning: `${tool === 'CobbAngle' ? 'Cobb angle' : 'Angle'}${label}`.trim(),
        value: v,
        unit: 'deg',
        unitCode: { value: 'deg', scheme: 'UCUM', meaning: 'degree' },
        index: i,
      })
      return
    }
    if (tool === 'EllipticalRoi' || tool === 'RectangleRoi' || tool === 'FreehandRoi') {
      const suv = data.suv
      if (suv && numOrNull(suv.mean) != null) {
        out.push({
          kind: 'suvMean',
          code: 'SUVMU',
          meaning: `SUV mean${label}`.trim(),
          value: suv.mean,
          unit: suv.unit || 'SUV',
          unitCode: { value: '{SUV}', scheme: 'UCUM', meaning: 'standardized uptake value' },
          index: i,
        })
      }
      if (suv && numOrNull(suv.max) != null) {
        out.push({
          kind: 'suvMax',
          code: 'SUVMAX',
          meaning: `SUV max${label}`.trim(),
          value: suv.max,
          unit: suv.unit || 'SUV',
          unitCode: { value: '{SUV}', scheme: 'UCUM', meaning: 'standardized uptake value' },
          index: i,
        })
      }
    }
  })
  return out
}

export function buildSrJson(ctx) {
  const image = (ctx && ctx.image) || {}
  const fileData = (ctx && ctx.fileData) || {}
  const observations = collectSrObservations(ctx && ctx.measurements)
  return {
    sopClassUid: ENHANCED_SR_SOP_CLASS,
    modality: 'SR',
    patientName: dsStr(image, 'x00100010') || 'ANONYMOUS',
    patientId: dsStr(image, 'x00100020') || '0',
    studyUid: dsStr(image, 'x0020000d') || (fileData.study && fileData.study.studyId) || '',
    referencedSop: dsStr(image, 'x00080018') || fileData.sopInstanceUID || '',
    observations,
  }
}

/**
 * @param {{ measurements: Array, image: object, fileData?: object }} ctx
 */
export function buildSrFile(ctx) {
  const image = (ctx && ctx.image) || {}
  const fileData = (ctx && ctx.fileData) || {}
  const payload = buildSrJson(ctx)
  const date = nowDicomDate()
  const time = nowDicomTime()
  const sopInstanceUid = newUid()
  const seriesUid = newUid()
  const studyUid = payload.studyUid || newUid()
  const refSop = payload.referencedSop
  const refClass = dsStr(image, 'x00080016') || '1.2.840.10008.5.1.4.1.1.2'

  const childItems = payload.observations.map((obs) => numItem(obs))

  const evidenceItem = refSop
    ? [
        concat([
          elUI('x0020000d', studyUid),
          elSQ('x00081115', [
            concat([
              elUI('x0020000e', dsStr(image, 'x0020000e') || seriesUid),
              elSQ('x00081140', [
                concat([elUI('x00081150', refClass), elUI('x00081155', refSop)]),
              ]),
            ]),
          ]),
        ]),
      ]
    : []

  const dataset = concat([
    elUI('x00080016', ENHANCED_SR_SOP_CLASS),
    elUI('x00080018', sopInstanceUid),
    elDA('x00080020', dsStr(image, 'x00080020') || date),
    elDA('x00080023', date),
    elTM('x00080030', dsStr(image, 'x00080030') || time),
    elTM('x00080033', time),
    elCS('x00080060', 'SR'),
    elLO('x0008103e', 'UDV Measurement SR'),
    elPN('x00100010', payload.patientName),
    elLO('x00100020', payload.patientId),
    elUI('x0020000d', studyUid),
    elUI('x0020000e', seriesUid),
    elSH('x00200010', dsStr(image, 'x00200010') || '1'),
    elCS('x0040a040', 'CONTAINER'),
    elSQ('x0040a043', [codeItem('UDV-SR', '99UDV', 'UDV Measurement Report')]),
    elCS('x0040a050', 'SEPARATE'),
    ...(evidenceItem.length ? [elSQ('x0040a375', evidenceItem)] : []),
    elCS('x0040a491', 'COMPLETE'),
    elCS('x0040a493', 'UNVERIFIED'),
    elSQ('x0040a730', childItems),
  ])

  const bytes = wrapPart10(dataset, {
    sopClassUid: ENHANCED_SR_SOP_CLASS,
    sopInstanceUid,
  })
  return {
    bytes,
    sopInstanceUid,
    sopClassUid: ENHANCED_SR_SOP_CLASS,
    json: payload,
  }
}
